import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  AgGrid,
  AgGridReact,
  Flex,
  Grid,
  LoadingIndicator,
  SearchOption,
  toast,
} from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetClientNameByIdQuery, useGetCurrentUserQuery } from "~/graphql/mainHooks";
import { useGetProgramsQuery, useUserScopedArenaIdsQuery } from "~/graphql/supergraphHooks";
import { LoadingCellRenderer } from "~/pages/Compliance/Common/LoadingCellRenderer";
import { useRefreshOnLinkCaseClose } from "~/pages/Compliance/Common/OnboardingStatus/useRefreshOnLinkCaseClose";
import { ComplianceSettingsDrawer } from "~/pages/Compliance/ComplianceSettingsDrawer";
import { useComplianceSettingsDrawer } from "~/pages/Compliance/ComplianceSettingsDrawer/useComplianceSettingsDrawer";
import ArenaSelector from "~/pages/Compliance/CreateCaseForm/CaseInformationCard/ArenaTypeahead";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import { isComplianceManager } from "~/utils/userRoles";
import { useEnableClientLevelPermissions } from "../flags";
import { dashboardContainer } from "../styles_vanilla.css";
import { getColumnDefs } from "./columnDefinition";
import { ComplianceEntitiesGraphqlDataSource } from "./complianceEntitiesGraphqlDataSource";
import { EntitiesExportButton } from "./ExportButton";
import { container } from "./styles_vanilla.css";

const CLIENT_ID_PARAM = "clientId";
const OUT_OF_SCOPE_MESSAGE = "You don't have access to this client's workspace.";

// Parse ?clientId= from the URL, treating missing/non-numeric values as absent.
const parseClientId = (search: string): number | null => {
  const raw = new URLSearchParams(search).get(CLIENT_ID_PARAM);
  const parsed = raw !== null ? Number(raw) : NaN;
  return Number.isInteger(parsed) ? parsed : null;
};

/**
 * Entities tab (COMP-4610). Behind a required client-search gate: the grid is empty until a
 * client (arena) is picked, then it loads that client's entities from `complianceEntitiesSlice`
 * on AG Grid SSRM (server-side sort, offset/limit pagination). Rendered only when the
 * `complianceEntityOnboardingEnabled` flag is on (gated by the parent route).
 *
 * The selected client is mirrored to the URL as `?clientId=<dbId>` so other surfaces (e.g. the
 * Clients tab) can deep-link straight to a client's entities. On deep-link we resolve the client
 * name from the id to pre-fill the search box.
 */
export const ComplianceEntitiesTable = () => {
  const history = useHistory();
  const location = useLocation();

  // Read the deep-linked client id once on mount; selections drive state, not the URL.
  const [initialClientId] = useState(() => parseClientId(location.search));
  const [arenaId, setArenaId] = useState<number | null>(initialClientId);
  const [clientName, setClientName] = useState<string | undefined>(undefined);

  const gridApiRef = useRef<AgGrid.GridApi | null>(null);

  // The client typeahead is sourced from main, which has no notion of compliance workspaces, so it
  // offers clients this user may not be scoped to. `complianceEntitiesSlice` is ArenaScoped and
  // would reject those, which the grid renders as a generic load failure. Check the caller's scope
  // up front instead and surface a specific message, matching CompliancePositionsTable.
  const clientLevelPermissionsEnabled = useEnableClientLevelPermissions();
  const {
    data: scopeData,
    loading: scopeLoading,
    error: scopeError,
  } = useUserScopedArenaIdsQuery({
    skip: !clientLevelPermissionsEnabled,
  });
  // null => scoping does not apply (flag off, or a non-compliance caller); an empty array => the
  // caller is scoped to no workspaces at all.
  //
  // If the scope query itself fails we cannot tell those apart, so we deliberately do not gate:
  // the backend remains the boundary, and an out-of-scope pick degrades to the generic grid
  // error it did before this change. Failing closed instead would tell every user they have no
  // access to every client on a transient error, which is both wrong and worse.
  const userScopedArenaIds = scopeError ? null : (scopeData?.userScopedArenaIds ?? null);

  const isArenaInScope = useCallback(
    (dbId: number) =>
      userScopedArenaIds === null ||
      userScopedArenaIds.includes(toGlobalId("ArenaObjectType", dbId)),
    [userScopedArenaIds],
  );

  // A deep-linked ?clientId= is applied on mount, before the caller's scope has loaded, so it can
  // only be checked once scope resolves. Derive the effective client rather than clearing state,
  // so an out-of-scope id never reaches the datasource. (Picking one in the typeahead is caught
  // synchronously in handleSelect, where scope is already known.)
  const arenaOutOfScope = !scopeLoading && arenaId !== null && !isArenaInScope(arenaId);
  const effectiveArenaId = arenaOutOfScope ? null : arenaId;

  const settingsDrawer = useComplianceSettingsDrawer({ arenaId: effectiveArenaId, clientName });

  // Only compliance managers can edit settings, so only they get the row-click drawer
  const { data: currentUserData } = useGetCurrentUserQuery({
    context: withClientContext("staff"),
  });
  const roles = (currentUserData?.currentUser?.roles?.edges ?? [])
    .map((edge) => edge.node.name)
    .filter((name): name is string => !!name);
  const canEditSettings = isComplianceManager(roles);

  // On deep-link we only have the id; resolve the name to pre-fill the search box.
  const { loading: resolvingClientName } = useGetClientNameByIdQuery({
    variables: {
      arenaIds:
        initialClientId !== null ? [toGlobalId("ArenaObjectType", initialClientId)] : [],
    },
    context: withClientContext("staff"),
    skip: initialClientId === null,
    onCompleted: (data) => {
      const name = data?.arenas?.edges?.[0]?.node?.name;
      if (name) {
        setClientName(name);
      }
    },
  });

  const dataSource = useMemo(
    () =>
      effectiveArenaId !== null
        ? new ComplianceEntitiesGraphqlDataSource(effectiveArenaId)
        : null,
    [effectiveArenaId],
  );
  // The Investor program checkbox list comes from the same query the pre-onboarding grid
  // filters on, so both views offer the same program options (COMP-5056).
  const { data: programsData } = useGetProgramsQuery();
  const investorProgramValues = useMemo(
    () =>
      (programsData?.programsAssignableToEntities ?? [])
        .map((program) => program.name)
        .filter((name): name is string => !!name),
    [programsData],
  );
  const columnDefs = useMemo(
    () => getColumnDefs(investorProgramValues),
    [investorProgramValues],
  );

  const setClientInUrl = useCallback(
    (dbId: number | null) => {
      const params = new URLSearchParams(location.search);
      if (dbId === null) {
        params.delete(CLIENT_ID_PARAM);
      } else {
        params.set(CLIENT_ID_PARAM, String(dbId));
      }
      history.replace({ pathname: location.pathname, search: params.toString() });
    },
    [history, location.pathname, location.search],
  );

  // Clearing the URL changes location.search, which rebuilds setClientInUrl and re-runs this
  // effect. arenaId is deliberately not cleared (the client stays selected in the search box),
  // so the out-of-scope condition still holds and the toast would repeat. Warn once per rejected
  // client instead.
  const warnedArenaIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!arenaOutOfScope || arenaId === null) return;
    if (warnedArenaIdRef.current === arenaId) return;
    warnedArenaIdRef.current = arenaId;
    setClientInUrl(null);
    toast.error(OUT_OF_SCOPE_MESSAGE);
  }, [arenaOutOfScope, arenaId, setClientInUrl]);

  const handleSelect = (option: SearchOption) => {
    if (option?.id) {
      const dbId = toDbId(String(option.id));
      if (!isArenaInScope(dbId)) {
        setArenaId(null);
        setClientName(option.label ?? undefined);
        setClientInUrl(null);
        toast.error(OUT_OF_SCOPE_MESSAGE);
        return;
      }
      setArenaId(dbId);
      setClientName(option.label ?? undefined);
      setClientInUrl(dbId);
    }
  };

  const handleClear = () => {
    setArenaId(null);
    setClientName(undefined);
    setClientInUrl(null);
  };

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: { type: "fitGridWidth" },
    rowModelType: "serverSide",
  };

  const getRowId = (params: AgGrid.GetRowIdParams) => String(params.data.id);

  const onGridReady = (event: AgGrid.GridReadyEvent) => {
    gridApiRef.current = event.api;
  };

  // SSRM caches rows, so a saved change refetches the grid rather than the Apollo cache
  const refreshGrid = () => gridApiRef.current?.refreshServerSide({ purge: true });

  useRefreshOnLinkCaseClose(gridApiRef, "/entities/link-case");

  // Hold render until the deep-linked client name resolves, so the search box mounts with it, and
  // until the caller's scope is known, so an out-of-scope deep link is dropped before the grid
  // mounts rather than loading and being rejected.
  if (resolvingClientName || scopeLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" style={{ height: "100%" }}>
        <LoadingIndicator />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap={16} className={container}>
      <Flex flexDirection="row" alignItems="center" justifyContent="space-between" gap={16}>
        <ArenaSelector
          placeholder="Search a client to view entities"
          initialSearchValue={clientName}
          maxWidth="360px"
          onSelectOption={handleSelect}
          onClearValue={handleClear}
        />
        <EntitiesExportButton arenaId={effectiveArenaId} clientName={clientName} />
      </Flex>
      {dataSource !== null && (
        <Flex className={dashboardContainer} data-testid="entities-grid">
          <Grid
            // Remount on client change so the SSRM datasource/cache is rebuilt cleanly.
            key={effectiveArenaId ?? "none"}
            getRowId={getRowId}
            gridOptions={gridOptions}
            columnDefs={columnDefs}
            pagination
            paginationPageSizeSelector={false}
            paginationPageSize={100}
            loadingCellRenderer={LoadingCellRenderer}
            rowModelType="serverSide"
            serverSideDatasource={dataSource}
            cacheBlockSize={100}
            onGridReady={onGridReady}
            onCellClicked={canEditSettings ? settingsDrawer.onCellClicked : undefined}
          />
        </Flex>
      )}
      <ComplianceSettingsDrawer
        subject="entity"
        row={settingsDrawer.row}
        onClose={settingsDrawer.onClose}
        onSaved={refreshGrid}
        defaultTab={settingsDrawer.defaultTab}
      />
    </Flex>
  );
};

export default ComplianceEntitiesTable;
